import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { toast } from "@/lib/toast";
import StepIndicator from "@/components/StepIndicator";
import PatientStep from "@/components/PatientStep";
import { useAuth } from "@/hooks/useAuth";
import FootageStep from "@/components/FootageStep";
import UpdateBanner from "@/components/UpdateBanner";
import AppHeader from "@/components/AppHeader";
import { trpc } from "@/lib/trpc";
import { saveHandle, loadHandle, deleteHandle } from "@/lib/sessionHandleStore";
import type { WizardState, ProcessedFile } from "@/pages/UploadWizard";

const STEPS = ["Provider Setup", "Upload Footage"];

/**
 * Provider A-Roll workflow — clones the Returning Patient flow but sources its
 * lookup from My Drive / Infinity / *Providers / {Region} / ... instead of
 * *Patients. The step components are shared with the patient flow via
 * PatientStep's `subjectKind="provider"` prop.
 */
export default function ProviderARoll() {
  const [, navigate] = useLocation();
  useAuth({ redirectOnUnauthenticated: true });
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardState, setWizardState] = useState<WizardState>({
    mode: "returning",
    driveFolderId: "",
    patientName: "",
    dateFolder: "",
    destinations: ["local"],
    sessionId: null,
    consentFormUploaded: false,
    localFolderHandle: null,
    localFolderPath: "",
    patientFolderHandle: null,
    localFolderLabel: "",
    subjectKind: "provider",
  });

  const createSession = trpc.sessions.create.useMutation();

  // Resume support — same query-param contract as UploadWizard.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resume = params.get("resume");
    if (resume !== "1") return;

    const sessionId = parseInt(params.get("sessionId") ?? "", 10);
    const patientName = params.get("patient") ?? "";
    const dateFolder = params.get("date") ?? "";
    const destinations = (params.get("destinations") ?? "").split(",").filter(Boolean);
    const gDriveDateFolderId = params.get("driveFolderId") ?? undefined;
    const gDriveFolderId = params.get("gDriveFolderId") ?? undefined;
    const gDriveFolderPath = params.get("gDriveFolderPath") ?? undefined;
    const localFolderLabel = params.get("folder") ?? "";
    const driveFolderId = gDriveFolderId ?? "";
    const videoTopic = params.get("topic") ?? undefined;

    // dateFolder is allowed to be empty in the auto-detect-from-metadata
    // flow — FootageStep handles per-file date routing on resume. We only
    // require the session row + provider identifiers here.
    if (!sessionId || !patientName) return;

    const restoreHandles = async () => {
      const patientFolderHandle = await loadHandle("resume_patientFolderHandle").catch(() => undefined);
      const localFolderHandle = await loadHandle("resume_localFolderHandle").catch(() => undefined);
      await deleteHandle("resume_patientFolderHandle").catch(() => {});
      await deleteHandle("resume_localFolderHandle").catch(() => {});

      setWizardState({
        mode: "returning",
        driveFolderId,
        patientName,
        dateFolder,
        destinations,
        sessionId,
        consentFormUploaded: false,
        localFolderHandle: localFolderHandle ?? null,
        localFolderPath: localFolderLabel,
        patientFolderHandle: patientFolderHandle ?? null,
        localFolderLabel,
        gDriveFolderId,
        gDriveFolderPath,
        gDriveDateFolderId,
        videoTopic,
        subjectKind: "provider",
      });
      setCurrentStep(1);
    };
    void restoreHandles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetupComplete = async (state: Partial<WizardState>) => {
    const updated = { ...wizardState, ...state };
    setWizardState(updated);

    // dateFolder is allowed to be empty in the auto-detect-from-metadata
    // flow — the actual MM.DD.YY value(s) are resolved per-file in
    // FootageStep, and the session row just acts as the provider-level FK
    // parent for footage rows.
    if (!updated.driveFolderId) {
      toast.error("Missing provider information");
      return;
    }

    try {
      const result = await createSession.mutateAsync({
        driveFolderId: updated.driveFolderId,
        dateFolder: updated.dateFolder ?? "",
      });
      setWizardState((prev) => ({ ...prev, ...updated, sessionId: result.id }));
      setCurrentStep(1);
    } catch (err: any) {
      const msg = err?.message ?? String(err);
      console.error("[ProviderARoll] createSession failed:", msg, err);
      toast.error(`Failed to create upload session: ${msg}`);
    }
  };

  const handleUploadComplete = async (
    processedFiles: ProcessedFile[],
    resolvedTopic?: string,
  ) => {
    const folderParam = wizardState.localFolderLabel
      ? `&folder=${encodeURIComponent(wizardState.localFolderLabel)}`
      : "";
    const destParam = wizardState.destinations.length > 0
      ? `&destinations=${encodeURIComponent(wizardState.destinations.join(","))}`
      : "";
    const driveFolderParam = wizardState.gDriveDateFolderId
      ? `&driveFolderId=${encodeURIComponent(wizardState.gDriveDateFolderId)}`
      : "";
    try {
      // `id` is the footage_files row id; Confirmation polls Pegasus status
      // against it for the per-file "AI saved" verification indicator.
      const thumbnailData = processedFiles.map((f) => ({
        id: f.id,
        thumbnail: f.thumbnail,
        thumbnailWidth: f.thumbnailWidth,
        thumbnailHeight: f.thumbnailHeight,
        status: f.status,
        name: f.renamedName || f.originalName,
      }));
      sessionStorage.setItem("confirmation_thumbnails", JSON.stringify(thumbnailData));
    } catch {
      // sessionStorage unavailable — thumbnails will fall back to icon
    }
    try {
      if (wizardState.patientFolderHandle) {
        await saveHandle("resume_patientFolderHandle", wizardState.patientFolderHandle);
      }
      if (wizardState.localFolderHandle) {
        await saveHandle("resume_localFolderHandle", wizardState.localFolderHandle);
      }
    } catch {
      // IDB unavailable
    }

    const sessionIdParam = wizardState.sessionId ? `&sessionId=${wizardState.sessionId}` : "";
    const gDriveFolderIdParam = wizardState.gDriveFolderId ? `&gDriveFolderId=${encodeURIComponent(wizardState.gDriveFolderId)}` : "";
    const gDriveFolderPathParam = wizardState.gDriveFolderPath ? `&gDriveFolderPath=${encodeURIComponent(wizardState.gDriveFolderPath)}` : "";
    // Forward the Video Topic so the Success page's "Add More This Session"
    // can carry it into the resumed wizard and keep landing in the same
    // suffixed folder. `resolvedTopic` is the FootageStep-final value
    // (either what the user typed up front or what they accepted from the
    // post-upload AI suggestion); fall back to wizardState if FootageStep
    // didn't pass one (older callers / unexpected flow).
    const effectiveTopic = (resolvedTopic ?? wizardState.videoTopic ?? "").trim();
    const topicParam = effectiveTopic
      ? `&topic=${encodeURIComponent(effectiveTopic)}`
      : "";
    const skippedFiles = processedFiles.filter((f) => f.status === "skipped");
    const skippedParam = skippedFiles.length > 0 ? `&skipped=${skippedFiles.length}` : "";
    // Collect the distinct date folders the batch actually landed in (auto-
    // detect-from-metadata mode may produce more than one). Mirrors the
    // patient-side UploadWizard logic so Provider Confirmation page also
    // shows the real folder(s) instead of "—".
    const resolvedDateFolders = Array.from(
      new Set(processedFiles.map((f) => f.dateFolder).filter((d): d is string => !!d)),
    ).sort();
    const effectiveDate =
      resolvedDateFolders.length === 1
        ? resolvedDateFolders[0]
        : wizardState.dateFolder || "";
    const datesParam =
      resolvedDateFolders.length > 1
        ? `&dates=${encodeURIComponent(resolvedDateFolders.join(","))}`
        : "";
    navigate(
      `/confirmation?kind=provider&patient=${encodeURIComponent(wizardState.patientName)}&date=${encodeURIComponent(effectiveDate)}${datesParam}&count=${processedFiles.length}${folderParam}${destParam}${driveFolderParam}${sessionIdParam}${gDriveFolderIdParam}${gDriveFolderPathParam}${topicParam}${skippedParam}&files=${encodeURIComponent(JSON.stringify(processedFiles.map((f) => f.renamedName || f.originalName)))}`
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <UpdateBanner />
      <AppHeader />

      {/*
       * Wrapper repaints the --primary accent to the Iowa blue-gray for
       * everything below the header. Designated colors (Minnesota purple,
       * All-Locations red, etc.) keep their explicit hex values.
       */}
      <div className="theme-provider-aroll">
        <div className="bg-white border-b border-border">
          <div className="container py-4">
            <StepIndicator steps={STEPS} currentStep={currentStep} />
          </div>
        </div>

        <main className="container py-8">
          <div className="max-w-2xl mx-auto">
            {currentStep === 0 && (
              <PatientStep
                wizardState={wizardState}
                onComplete={handleSetupComplete}
                isLoading={createSession.isPending}
                subjectKind="provider"
              />
            )}
            {currentStep === 1 && wizardState.sessionId && (
              <FootageStep
                wizardState={wizardState}
                onComplete={handleUploadComplete}
                onBack={() => setCurrentStep(0)}
                subjectKind="provider"
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
